import React from 'react';
import api from '../services/api';

// Production-facing Sales Plan — SCHEDULE ONLY. Pricing columns are absent (not blanked): the
// backend (/finance/sales-plan) strips selling_price/revenue for non-costing roles, and this view
// never renders a price column. For the production team's shipment-deadline planning.
//
// Carry-forward is DISPLAY ONLY (Rajan's ruling): the stored sales_plan row is NEVER rewritten —
// it keeps its original plan_month; the current-month view surfaces an earlier, not-fully-shipped
// row as overdue. Carry test: planned_shipment_date is in a month EARLIER than the viewed month AND
// the unshipped balance (order_qty − COALESCE(actual_qty_shipped,0)) is > 0. Partial shipments carry
// only the UNSHIPPED BALANCE. Buyer free-text is shown as-is and NEVER grouped/totalled (Rajan is
// cleaning it separately; "life style" and "Lifestyle Pvt Ltd" are still distinct strings).
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const monthOf = (d) => (d ? String(d).slice(0, 7) : '');           // 'YYYY-MM' (lexical order = chronological)
const dayOf = (d) => (d ? String(d).slice(0, 10) : '');
const balanceOf = (r) => num(r.order_qty) - num(r.actual_qty_shipped);   // unshipped balance
const isCarry = (r, m) => !!r.planned_shipment_date && monthOf(r.planned_shipment_date) < m && balanceOf(r) > 0.0005;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMonth = (m) => { if (!m) return '—'; const [y, mm] = m.split('-'); const i = parseInt(mm, 10) - 1; return (MONTHS[i] || mm) + ' ' + y; };
const inr = (v) => num(v).toLocaleString('en-IN');

const th = { padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const td = { padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };

function Tile({ label, qty, count, tone }) {
  const bg = tone === 'red' ? '#FDECEC' : tone === 'green' ? '#E8F5EC' : tone === 'amber' ? '#FEF3E2' : '#F4F5FA';
  const fg = tone === 'red' ? '#A32D2D' : tone === 'green' ? '#166534' : tone === 'amber' ? '#9A6B00' : '#3B3A8F';
  return (
    <div style={{ flex: '1 1 180px', background: bg, borderRadius: 10, padding: '12px 16px', minWidth: 160 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: fg, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: fg, marginTop: 2 }}>{inr(qty)}</div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{count} row{count === 1 ? '' : 's'}</div>
    </div>
  );
}

export default function SalesPlanView() {
  const [rows, setRows] = React.useState([]);
  const curMonth = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = React.useState(curMonth);   // default: current month (carry-forward view)
  const [range, setRange] = React.useState({ from: '', to: '' });

  React.useEffect(() => {
    // Fetch ALL rows (carry-forward needs earlier-month rows) — filtering is entirely client-side.
    api.get('/finance/sales-plan').then(r => setRows(r.data.data || [])).catch(() => {});
  }, []);

  const rangeActive = !!(range.from || range.to);
  const refMonth = month || curMonth;                    // month used for carry-forward + tiles (non-range)

  // Mutually exclusive filters.
  const onMonth = (v) => { setMonth(v); setRange({ from: '', to: '' }); };
  const onRange = (patch) => { setRange(r => ({ ...r, ...patch })); setMonth(''); };
  const clearAll = () => { setMonth(curMonth); setRange({ from: '', to: '' }); };

  // ── Build the displayed rows ─────────────────────────────────────────────────
  let display;
  if (rangeActive) {
    // Custom range: exactly what falls in it (planned_shipment_date), NO carry-forward.
    display = rows.filter(r => {
      const d = dayOf(r.planned_shipment_date); if (!d) return false;
      if (range.from && d < range.from) return false;
      if (range.to && d > range.to) return false;
      return true;
    }).map(r => ({ ...r, _carry: false }));
    display.sort((a, b) => dayOf(a.planned_shipment_date).localeCompare(dayOf(b.planned_shipment_date)));
  } else if (month) {
    // Month mode: this month's rows (by planned_shipment_date) PLUS earlier carried rows.
    display = rows
      .filter(r => monthOf(r.planned_shipment_date) === month || isCarry(r, month))
      .map(r => ({ ...r, _carry: isCarry(r, month) }));
    display.sort((a, b) => (a._carry === b._carry
      ? dayOf(a.planned_shipment_date).localeCompare(dayOf(b.planned_shipment_date))
      : (a._carry ? -1 : 1)));   // carried rows to the TOP
  } else {
    // All rows (month cleared, no range): highlight anything overdue vs the current month.
    display = rows.map(r => ({ ...r, _carry: isCarry(r, curMonth) }));
    display.sort((a, b) => (a._carry === b._carry
      ? dayOf(b.planned_shipment_date).localeCompare(dayOf(a.planned_shipment_date))
      : (a._carry ? -1 : 1)));
  }

  // ── Tiles (respect the active filter) ────────────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  const in7 = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const dueSoon = (r) => { const d = dayOf(r.planned_shipment_date); return d && d >= today && d <= in7; };

  let plannedRows, shippedRows, carriedRows;
  if (rangeActive) {
    plannedRows = display;   // planned qty within the range
    shippedRows = rows.filter(r => { const d = dayOf(r.actual_shipment_date); return d && (!range.from || d >= range.from) && (!range.to || d <= range.to); });
    carriedRows = [];        // no carry-forward in a custom range
  } else {
    plannedRows = rows.filter(r => monthOf(r.planned_shipment_date) === refMonth);
    shippedRows = rows.filter(r => monthOf(r.actual_shipment_date) === refMonth);
    carriedRows = rows.filter(r => isCarry(r, refMonth));
  }
  const dueRows = display.filter(dueSoon);

  const sum = (arr, f) => arr.reduce((s, r) => s + f(r), 0);
  const plannedLabel = rangeActive ? 'Planned (range)' : 'Planned this month';
  const shippedLabel = rangeActive ? 'Shipped (range)' : 'Shipped this month';

  const inp = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 };

  return (
    <div style={{ padding: 24, maxWidth: 1180, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 4px', color: '#1a1a2e' }}>📈 Sales Plan — Shipment Schedule</h2>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>Order shipment schedule for production planning. Pricing is not shown. Carry-forward is display-only — the stored plan is never changed.</div>

      {/* Filters — month OR date range (mutually exclusive) */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14, background: '#F7F6F3', padding: '8px 12px', borderRadius: 8 }}>
        <label style={{ fontSize: 12, color: '#555' }}>Month&nbsp;
          <input type="month" value={month} onChange={e => onMonth(e.target.value)} style={inp} /></label>
        <span style={{ color: '#bbb', fontSize: 12 }}>— or a date range —</span>
        <label style={{ fontSize: 12, color: '#555' }}>From&nbsp;
          <input type="date" value={range.from} onChange={e => onRange({ from: e.target.value })} style={inp} /></label>
        <label style={{ fontSize: 12, color: '#555' }}>To&nbsp;
          <input type="date" value={range.to} onChange={e => onRange({ to: e.target.value })} style={inp} /></label>
        <button onClick={clearAll} style={{ ...inp, cursor: 'pointer', background: '#fff' }}>Reset to this month</button>
        <span style={{ fontSize: 12, color: '#888', marginLeft: 'auto' }}>
          {rangeActive ? `Range ${range.from || '…'} → ${range.to || '…'} (no carry-forward)` : (month ? `Viewing ${fmtMonth(month)} + carried` : 'All rows')}
        </span>
      </div>

      {/* Tiles */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Tile label={plannedLabel} qty={sum(plannedRows, r => num(r.order_qty))} count={plannedRows.length} tone="blue" />
        <Tile label={shippedLabel} qty={sum(shippedRows, r => num(r.actual_qty_shipped))} count={shippedRows.length} tone="green" />
        <Tile label="Carried forward" qty={sum(carriedRows, balanceOf)} count={carriedRows.length} tone="red" />
        <Tile label="Due this week" qty={sum(dueRows, r => num(r.order_qty))} count={dueRows.length} tone="amber" />
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['Plan No', 'GPO', 'Style', 'Description', 'Color', 'Buyer', 'Plan Month', 'Order Qty', 'Shipped', 'Balance', 'Planned Ship', 'Actual Ship', 'Status'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {display.length === 0 && <tr><td colSpan={13} style={{ ...td, textAlign: 'center', color: '#aaa' }}>No sales plans for this view.</td></tr>}
          {display.map(r => {
            const bal = balanceOf(r);
            const rowStyle = r._carry ? { background: '#FDECEC', borderLeft: '3px solid #E22D2D' } : {};
            return (
              <tr key={r.id} style={rowStyle}>
                <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>
                  {r.plan_no}
                  {r._carry && <div><span style={{ display: 'inline-block', marginTop: 3, padding: '1px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: '#E22D2D', color: '#fff' }}>
                    Carried fwd from {fmtMonth(r.plan_month || monthOf(r.planned_shipment_date))}</span></div>}
                </td>
                <td style={td}>{r.gpo_no || '—'}</td>
                <td style={td}>{r.style_no || '—'}</td>
                <td style={td}>{r.style_description || '—'}</td>
                <td style={td}>{r.color || '—'}</td>
                <td style={td}>{r.buyer || '—'}</td>
                <td style={td}>{r.plan_month || '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>{r.order_qty != null ? inr(r.order_qty) : '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>{r.actual_qty_shipped != null ? inr(r.actual_qty_shipped) : '—'}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: r._carry ? 700 : 400, color: r._carry ? '#A32D2D' : (bal > 0.0005 ? '#333' : '#166534') }}>{inr(bal)}</td>
                <td style={td}>{dayOf(r.planned_shipment_date) || '—'}</td>
                <td style={td}>{dayOf(r.actual_shipment_date) || '—'}</td>
                <td style={td}><span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#EEEDF8', color: '#3B3A8F' }}>{r.status || '—'}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
